import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function IntegrationsGridSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const card = getCardClass(cardStyle, s.dark);
    const items = content.items || [];
    const variant = config.variant || 'grid';
    const columns = config.columns || 4;
    const hubText = content.hub_text || '';

    if (items.length === 0) return null;

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    const gridCls = columns === 3 ? 'grid-cols-2 sm:grid-cols-3'
        : columns === 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5'
        : columns === 6 ? 'grid-cols-3 sm:grid-cols-4 lg:grid-cols-6'
        : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-4';

    // ── Item card ──
    const renderItem = (item, i) => {
        const inner = (
            <div className={`${card} p-5 flex flex-col items-center text-center group transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: visible ? `${150 + i * 60}ms` : '0ms' }}>
                {/* Logo / Avatar */}
                <div className="w-16 h-16 rounded-xl flex items-center justify-center overflow-hidden mb-3 transition-transform duration-300 group-hover:scale-110"
                    style={{ backgroundColor: hexToRgba(s.accent, 0.06) }}>
                    {item.image_url ? (
                        <img src={item.image_url} alt={item.name || ''} className="w-12 h-12 object-contain" loading="lazy" />
                    ) : (
                        <span className="text-xl font-bold" style={{ color: s.accent }}>
                            {(item.name || '?').charAt(0).toUpperCase()}
                        </span>
                    )}
                </div>
                <span className="text-sm font-medium" style={{ color: s.text }}>{item.name}</span>
                {item.category && (
                    <span className="text-[10px] mt-1 px-2 py-0.5 rounded-full" style={{ backgroundColor: hexToRgba(s.secondary, 0.1), color: s.secondary }}>
                        {item.category}
                    </span>
                )}
            </div>
        );

        if (item.link) {
            return <a key={i} {...resolveLinkProps(item.link)} className="block">{inner}</a>;
        }
        return <div key={i}>{inner}</div>;
    };

    // ── Categories variant ──
    if (variant === 'categories') {
        const grouped = {};
        items.forEach(item => {
            const cat = item.category || 'Otros';
            if (!grouped[cat]) grouped[cat] = [];
            grouped[cat].push(item);
        });
        const categoryNames = Object.keys(grouped);

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="space-y-12">
                        {categoryNames.map((cat, ci) => (
                            <div key={cat}>
                                <h3 className="text-lg font-bold mb-6 flex items-center gap-3" style={{ color: s.text }}>
                                    <span className="w-1.5 h-6 rounded-full" style={{ backgroundColor: s.accent }} />
                                    {cat}
                                </h3>
                                <div className={`grid ${gridCls} gap-4`}>
                                    {grouped[cat].map((item, i) => renderItem(item, ci * 10 + i))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Radial variant ──
    if (variant === 'radial') {
        const count = items.length;
        const radius = 42; // percentage from center

        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                    {/* Mobile: grid fallback */}
                    <div className="md:hidden">
                        {hubText && (
                            <div className="flex justify-center mb-6">
                                <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-xl"
                                    style={{ backgroundColor: s.accent }}>
                                    {hubText}
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                            {items.map((item, i) => renderItem(item, i))}
                        </div>
                    </div>

                    {/* Desktop: radial layout */}
                    <div className="hidden md:block">
                        <div className="relative mx-auto" style={{ width: '600px', height: '600px', maxWidth: '100%' }}>
                            {/* Connecting lines */}
                            {items.map((_, i) => {
                                const angle = (360 / count) * i - 90;
                                const rad = (angle * Math.PI) / 180;
                                const x2 = 50 + radius * Math.cos(rad);
                                const y2 = 50 + radius * Math.sin(rad);
                                return (
                                    <svg key={`line-${i}`} className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                                        <line x1="50%" y1="50%" x2={`${x2}%`} y2={`${y2}%`}
                                            stroke={s.dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb'} strokeWidth="1" strokeDasharray="4 4" />
                                    </svg>
                                );
                            })}

                            {/* Center hub */}
                            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
                                <div className={`w-24 h-24 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-2xl transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
                                    style={{ backgroundColor: s.accent }}>
                                    {hubText || (
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.07-9.07a4.5 4.5 0 016.364 6.364l-4.5 4.5a4.5 4.5 0 01-7.244-1.242" />
                                        </svg>
                                    )}
                                </div>
                            </div>

                            {/* Orbiting items */}
                            {items.map((item, i) => {
                                const angle = (360 / count) * i - 90;
                                const rad = (angle * Math.PI) / 180;
                                const x = 50 + radius * Math.cos(rad);
                                const y = 50 + radius * Math.sin(rad);

                                return (
                                    <div key={i}
                                        className={`absolute -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`}
                                        style={{
                                            left: `${x}%`,
                                            top: `${y}%`,
                                            transitionDelay: visible ? `${200 + i * 80}ms` : '0ms',
                                        }}>
                                        {item.link ? (
                                            <a {...resolveLinkProps(item.link)} className="block group">
                                                <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl border-2`}
                                                    style={{
                                                        backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff',
                                                        borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                                                    }}>
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt={item.name || ''} className="w-10 h-10 object-contain rounded-full" loading="lazy" />
                                                    ) : (
                                                        <span className="text-lg font-bold" style={{ color: s.accent }}>{(item.name || '?').charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <p className="text-[11px] font-medium text-center mt-1.5 max-w-[80px] truncate" style={{ color: s.textSub }}>{item.name}</p>
                                            </a>
                                        ) : (
                                            <div className="group cursor-default">
                                                <div className={`w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl border-2`}
                                                    style={{
                                                        backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#ffffff',
                                                        borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                                                    }}>
                                                    {item.image_url ? (
                                                        <img src={item.image_url} alt={item.name || ''} className="w-10 h-10 object-contain rounded-full" loading="lazy" />
                                                    ) : (
                                                        <span className="text-lg font-bold" style={{ color: s.accent }}>{(item.name || '?').charAt(0).toUpperCase()}</span>
                                                    )}
                                                </div>
                                                <p className="text-[11px] font-medium text-center mt-1.5 max-w-[80px] truncate" style={{ color: s.textSub }}>{item.name}</p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Grid variant (default) ──
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                {hubText && (
                    <div className={`flex justify-center mb-10 transition-all duration-500 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`}>
                        <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-xl"
                            style={{ backgroundColor: s.accent }}>
                            {hubText}
                        </div>
                    </div>
                )}
                <div className={`grid ${gridCls} gap-4`}>
                    {items.map((item, i) => renderItem(item, i))}
                </div>
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
